/*
  SDS Core-Charge auto-deposit (Globo core-charge replacement)
  ------------------------------------------------------------
  Keeps the hidden "Refundable Core Charge" deposit line in the cart mirrored to
  the core products that require it. No app, no Globo.

  Core products carry hidden line-item properties (snippets/sds-core-deposit-props):
     _core_deposit_variant = numeric variant id of the matching CORE-<amt> deposit
     _core_qty             = deposit units required per unit of the core product

  TWO mechanisms:

  1) ATOMIC ADD (fetch interceptor). When a /cart/add carries _core_deposit_variant,
     we rewrite the request so the core item AND its deposit are added in the SAME
     /cart/add call. This matters when a checkout/cart validation app (e.g. BoomGate)
     requires the deposit to be present: if we added the core item alone first, the
     validation would reject it before the deposit could attach. Adding both together
     means the resulting cart always contains the pair, so it passes.

  2) RECONCILE (cart:change). After any cart change we read /cart.js and make the
     deposit line quantities exactly mirror the core lines (qty x _core_qty), handling
     quantity edits and removals. Idempotent and guarded, so it never loops and does
     nothing when already in sync.

  Safe by design: every network op and the interceptor are wrapped in try/catch and
  fall back to normal behavior on any error, so the cart can never break.

  Remove this file (+ the props/notice snippets and the loader) once the core-charge
  migration is retired.
*/
(function () {
  'use strict';

  var DEPOSIT_PRODUCT_ID = 15412789215520;
  var ADD_RE = /\/cart\/add(\.js)?(\?|$)/;

  // ---- deposit item for a given set of line-item properties + quantity ----
  function depositFor(props, qty) {
    if (!props) return null;
    var dv = props._core_deposit_variant;
    if (!dv || props._core_auto) return null;
    var vid = parseInt(dv, 10);
    if (!vid) return null;
    var per = parseInt(props._core_qty, 10);
    if (!(per > 0)) per = 1;
    return { id: vid, quantity: (qty || 1) * per, properties: { _core_auto: '1' } };
  }

  // ---------------------------------------------------------------------------
  // 1) ATOMIC ADD — intercept fetch('/cart/add') and inject the deposit item
  // ---------------------------------------------------------------------------
  var origFetch = window.fetch;
  if (typeof origFetch === 'function') {
    window.fetch = function (input, init) {
      try {
        var url = (typeof input === 'string') ? input : (input && input.url) || '';
        var method = (init && init.method) || (input && input.method) || 'GET';
        if (url && ADD_RE.test(url) && String(method).toUpperCase() === 'POST' && init && init.body) {
          var body = init.body;

          // Native theme add: FormData (id, quantity, properties[...], sections, sections_url)
          if (typeof FormData !== 'undefined' && body instanceof FormData) {
            var id = body.get('id');
            var quantity = parseInt(body.get('quantity'), 10) || 1;
            var props = {};
            body.forEach(function (v, k) {
              var m = k.match(/^properties\[(.+)\]$/);
              if (m) props[m[1]] = v;
            });
            var dep = depositFor(props, quantity);
            if (dep && id) {
              var payload = { items: [{ id: parseInt(id, 10), quantity: quantity, properties: props }, dep] };
              var sections = body.get('sections');
              if (sections) payload.sections = sections;
              var su = body.get('sections_url');
              if (su) payload.sections_url = su;
              var newInit = Object.assign({}, init, {
                body: JSON.stringify(payload),
                headers: Object.assign({}, init.headers || {}, {
                  'Content-Type': 'application/json',
                  'X-Requested-With': 'XMLHttpRequest'
                })
              });
              return origFetch.call(window, url, newInit);
            }
          }

          // JSON body ({items:[...]} or single {id, quantity, properties})
          else if (typeof body === 'string') {
            var data = null;
            try { data = JSON.parse(body); } catch (e) { data = null; }
            if (data) {
              var items = data.items ? data.items.slice() : (data.id ? [data] : null);
              if (items) {
                var extra = [];
                items.forEach(function (it) {
                  var d = depositFor(it.properties, it.quantity || 1);
                  if (d) extra.push(d);
                });
                if (extra.length) {
                  var base = data.items ? data : { items: items };
                  base.items = items.concat(extra);
                  var ni = Object.assign({}, init, { body: JSON.stringify(base) });
                  return origFetch.call(window, url, ni);
                }
              }
            }
          }
        }
      } catch (e) { /* fall through to normal fetch */ }
      return origFetch.apply(window, arguments);
    };
  }

  // ---------------------------------------------------------------------------
  // 2) RECONCILE — keep deposit line qty mirrored to core lines
  // ---------------------------------------------------------------------------
  var running = false, queued = false;

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }
  function changeLine(key, qty) {
    return fetch('/cart/change.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    });
  }
  function addDeposit(variantId, qty) {
    return fetch('/cart/add.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: qty, properties: { _core_auto: '1' } }] })
    }).then(function (r) { if (!r.ok) throw new Error('deposit add ' + r.status); return r; });
  }

  function computePlan(cart) {
    var items = (cart && cart.items) || [];
    var desired = {};   // variantId -> required qty
    var current = {};   // variantId -> [{key, qty}]
    items.forEach(function (li) {
      if (li.product_id === DEPOSIT_PRODUCT_ID) {
        (current[li.variant_id] = current[li.variant_id] || []).push({ key: li.key, qty: li.quantity });
        return;
      }
      var props = li.properties || {};
      var dv = props._core_deposit_variant;
      if (!dv) return;
      var vid = parseInt(dv, 10);
      if (!vid) return;
      var per = parseInt(props._core_qty, 10);
      if (!(per > 0)) per = 1;
      desired[vid] = (desired[vid] || 0) + li.quantity * per;
    });
    var changes = [], adds = [];
    Object.keys(current).forEach(function (vid) {
      var want = desired[vid] || 0;
      current[vid].forEach(function (ln, idx) {
        var target = idx === 0 ? want : 0;
        if (ln.qty !== target) changes.push({ key: ln.key, qty: target });
      });
    });
    Object.keys(desired).forEach(function (vid) {
      if (!current[vid] && desired[vid] > 0) adds.push({ id: parseInt(vid, 10), qty: desired[vid] });
    });
    return { changes: changes, adds: adds };
  }

  function applyPlan(plan) {
    var chain = Promise.resolve();
    plan.changes.forEach(function (c) { chain = chain.then(function () { return changeLine(c.key, c.qty); }); });
    plan.adds.forEach(function (a) { chain = chain.then(function () { return addDeposit(a.id, a.qty); }); });
    return chain;
  }

  function reconcile() {
    if (running) { queued = true; return; }
    running = true;
    getCart().then(function (cart) {
      var plan = computePlan(cart);
      if (!plan.changes.length && !plan.adds.length) return null;
      return applyPlan(plan).then(function () {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      });
    }).catch(function () { /* never break the cart */ })
      .then(function () { running = false; if (queued) { queued = false; reconcile(); } });
  }

  document.addEventListener('cart:change', function () { reconcile(); });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { reconcile(); });
  } else {
    reconcile();
  }
})();
